import { Dialog, DialogActions, DialogContent, TextField } from '@mui/material';
import { MprcButton, MprcContainer, MprcDialogTitleClose, MprcRequestErrorDialog, MprcRequestIndicator } from 'components';
import { AutoCompleteDataModel, AutoCompleteSuggestions } from 'components/MprcAutoComplete';
import { MprcResolveRequestErrors } from 'components/MprcRequestErrorReport';
import { GroupUsages, MemberType, UserSelectionEntityType } from 'generated/CoreApi_v1';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GroupAutoComplete } from 'views/foundation/group/components/GroupAutoComplete';
import { UserAutoComplete } from 'views/foundation/user/components/UserAutoComplete';


////////////////////////////////////////////////////////////////////////////////

interface IProps {
    publicationId: string;
    title: string;
    memberType: MemberType;
    onClose: () => void;
}

////////////////////////////////////////////////////////////////////////////////

export const RecommendPublicationDialog: React.FC<IProps> = (props) => {

    const { publicationId, memberType, title, onClose } = props;

    const { t } = useTranslation("knowledge");
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();

    const [text, setText] = useState<string | null>(null);
    const [viewError, setViewError] = useState<any>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [selectedItems, setSelectedItems] = useState<AutoCompleteDataModel[]>([]);

    const canSave = selectedItems.length > 0;

    ///
    const handleSave = () => {

        if (!canSave) return;

        setSaving(true);
        api.v1.knowledgePublication.addRecommendation({
            publicationId,
            memberIds: selectedItems.map(x => x.key),
            memberType,
            comments: text
        })
            .then(result => {
                if (result.data === true) {
                    enqueueSnackbar(t("Feed.Card.Actions.RecommendPublicationDialog.SavedSuccessMessage"), { variant: "success" });
                    onClose();
                } else {
                    enqueueSnackbar(t("Feed.Card.Actions.RecommendPublicationDialog.SavedFailMessage"), { variant: "error" });
                }
            })
            .catch(e => {
                const r = MprcResolveRequestErrors(e);
                if (r.fieldsWithError != undefined && (r.fieldsWithError["publicationId"] || r.fieldsWithError["memberIds"])) {
                    const errors = (r.fieldsWithError["publicationId"] ?? []).concat(r.fieldsWithError["memberIds"] ?? []);
                    errors.forEach(item => enqueueSnackbar(item, { variant: "error" }));
                }
                else {
                    setViewError(e);
                }
            }).finally(() => {
                setSaving(false);
            });
    }

    return (
        <Dialog
            open
            onClose={() => onClose()}
            maxWidth="xs"
            fullWidth
        >
            <MprcDialogTitleClose
                title={t("Feed.Card.Actions.RecommendPublicationDialog.Title")}
                subtitle={title}
                onClose={() => onClose()}
                shrinkPadding
            />
            <DialogContent>
                <MprcRequestIndicator type="post" visible={saving} />
                <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />
                <MprcContainer>
                    {memberType == MemberType.User ?
                        <UserAutoComplete
                            autoFocus
                            selectedItems={selectedItems}
                            onChange={items => setSelectedItems(items)}
                            multiSelect={true}
                            placeholder={t("Feed.Card.Actions.RecommendPublicationDialog.UsersPlaceholder")}
                            label={t("Feed.Card.Actions.RecommendPublicationDialog.UsersLabel")}
                            selectionHistoryType={UserSelectionEntityType.UserPublicationRecommendation}
                            required
                        />
                        :
                        <GroupAutoComplete
                            autoFocus
                            selectedItems={selectedItems}
                            groupUsages={GroupUsages.ContentSharing}
                            onChange={items => setSelectedItems(items)}
                            multiSelect={true}
                            placeholder={t("Feed.Card.Actions.RecommendPublicationDialog.GroupsPlaceholder")}
                            label={t("Feed.Card.Actions.RecommendPublicationDialog.GroupsLabel")}
                            required
                            onGetDataCustom={({ searchText, pageNumber, pageSize }, signal) =>
                                new Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>((resolve, reject) => {
                                    api.v1.knowledgeSuggestions.publicationRecommendationGroups({
                                        searchText: searchText ?? null,
                                        pageNumber,
                                        pageSize,
                                    }, { signal })
                                        .then(result => resolve(result.data))
                                        .catch(e => reject(e));
                                })
                            }
                        />
                    }
                </MprcContainer>
                <TextField
                    value={text}
                    placeholder={t("Feed.Card.Actions.RecommendPublicationDialog.CommentPlaceholder")}
                    multiline={true}
                    rows={10}
                    onChange={(e) => setText(e.target.value)}
                    fullWidth
                    variant="outlined"
                />
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", alignItems: "center" }}>
                <MprcButton
                    text={t("Feed.Card.Actions.RecommendPublicationDialog.SaveButtonText")}
                    title={t("Feed.Card.Actions.RecommendPublicationDialog.SaveButtonTooltip")}
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={!canSave}
                />
            </DialogActions>
        </Dialog>
    );
}
